/* eslint-disable no-param-reassign */
import { Analytics as AnalyticsSDK } from '@superbet-group/web.analytics.sdk';

import LimitError from '@core/errors/user/LimitError';
import { OnlineDepositType } from '@core/mappers/transaction/types';
import { isIdCardVerificationCheckRequired } from '@core/models/user/UserDataService';
import { antiFraudService } from '@core/services/antiFraud/AntiFraudService';
import {
    TransactionType,
    AnalyticsEvent,
} from '@superbet-group/web.types';
import { UserService } from '@core/services/user/UserService';
import { errorGuard } from '@core/utils/ErrorGuard';
import { shellLocalConfig } from '@src/config/shell/localConfig';
import { openRequirements } from '@shared/userProfile/idCardVerification/IdCardVerificationRequired';
import {
    AnalyticsKycEvent,
    sendVerifyIdentityEvent,
} from '@accountFederation/modules/idCardVerification/analytics/idVerificationAnalytics';
import { getIdVerificationStoreGetters } from '@accountFederation/modules/idCardVerification/analytics/idVerificationStoreGetters';
import { verifyIdentityEntrypoints } from '@accountFederation/modules/idCardVerification/types';
import modalCodes from '@store/modules/ui/shared/modal/modalCodes';
import { getConfig } from '@superbet-group/web.lib.config';
import { featureFlagService } from '@superbet-group/web.lib.flags';
import BonusProvider from '@core/models/bonus/BonusProvider';
import {
    ValidationError,
    loadScript,
} from '@superbet-group/web.lib.utilities';
import { marketingSourceService } from '@core/services/marketing/MarketingSourceService';
import { ftdAttributionHistory } from '@core/services/marketing/_private/constants';
import { processOnlineTransactionResponse } from '@modules/onlinePayment';
// eslint-disable-next-line import/no-unresolved
import DepositError from '@core/errors/user/DepositError';
import {
    PixDepositDto,
    OnlineTransactionDto,
    TransactionDtoType,
} from '@core/mappers/transaction/dtos/transactionDtos';
import User from '@core/models/user/User';
import Payment from '@core/models/user/pushNotifications/Payment';
import { PaymentStatus } from '@walletFederation/src/modules/payments/enums';
import { sha256 } from '@src/app/utils/sha256';
import { ModalCode } from '@walletFederation/src/modals/ModalCodes';
import store from '@app/store';

const applePayScriptPath = `${window.location.origin}/static/js/applepay.js`;

// eslint-disable-next-line sonarjs/cognitive-complexity
export async function submit(component: any, onSuccess: (transactionId?: string, depositedAmount?: number) => void) {
    const hasIncompleteVerification = store.getters['data/captiveKyc/hasIncompleteVerification'];

    if (hasIncompleteVerification) {
        store.dispatch('data/captiveKyc/showCaptiveKycEntry');
        return;
    }

    component.isLoading = true;

    if (component.isQrBancontact) {
        component.isBancontactLoading = true;
    }

    // Load and wait for applepay wrapper needed by iframe only if apple pay is supported
    if (window.ApplePaySession) {
        await loadScript(applePayScriptPath, 'applepay');
    }

    await errorGuard(async () => {
        // because we have one additional online deposit on account page!
        if (
            component.showWelcomeBonus
            && getConfig().countrySpecific.user.bonus.bonusProvider === BonusProvider.Comtrade
        ) {
            component.setModal(modalCodes.welcomeBonus);
        } else if (isIdCardVerificationCheckRequired(component.user, TransactionType.Deposit)) {
            openRequirements(
                {
                    hasIdCardIntro: true,
                    isRemainingTimeVisible: getConfig()
                        .countrySpecific.user.deposit.idCardVerificationCheck.isRemainingTimeVisible,
                    actionType: TransactionType.Deposit,
                },
                undefined,
                component.$t('pam.kyc.intro.depositTerms'),
            );
            sendVerifyIdentityEvent({
                eventName: AnalyticsKycEvent.VerifyIdentityEntry,
                source: verifyIdentityEntrypoints.Deposit,
                idVerificationStoreGetters: getIdVerificationStoreGetters(),
            });
        } else {
            const isRoutedPayments = await featureFlagService.getFeatureValue<string>('wallet.configuration.payments-api-routing', '0');
            const { session, sessionId } = antiFraudService.getCurrentSession();
            const isPixPaymentType = component.paymentType === OnlineDepositType.Pix;
            const isBancontactPaymentType = component.paymentType === OnlineDepositType.Bancontact && component.isQrBancontact;
            const routedParams = {
                paymentMethod: component.paymentType,
                paymentRouterVersion: 'v2', // routing version available
            };

            AnalyticsSDK.getInstance()
                .log('Deposit_Attempt', {
                    amount: component.fields.amount.value,
                    paymentType: component.paymentType,
                });

            const { paymentUrl, transactionId, appUrl } = await UserService.getInstance().createDeposit(
                component.fields.amount.value,
                component.paySafeCard,
                component.paymentType === OnlineDepositType.PaymentIQ,
                'static/html/paymentGatewayStatus.html',
                session,
                sessionId,
                isPixPaymentType || isBancontactPaymentType,
                isBancontactPaymentType ? OnlineDepositType.Bancontact : null,
                isRoutedPayments ? routedParams : null,
            );

            if (isPixPaymentType || isBancontactPaymentType) {
                if (component.isAppRedirectRequest && appUrl) {
                    component.openRedirectUrl(appUrl);
                }

                component.setModal({
                    code: ModalCode.qrDeposit,
                    isClosable: false,
                    data: {
                        paymentUrl,
                        amount: component.fields.amount.value,
                        type: component.paymentType,
                    },
                });
                onSuccess(transactionId, component.fields.amount.value);
            } else {
                component.setModal({
                    code: ModalCode.depositResponse,
                    data: paymentUrl,
                });
            }
        }
    }, {
        onError: (error) => {
            if (error instanceof ValidationError) {
                component.asyncErrors = error.fieldErrors;
            } else if (error instanceof LimitError && shellLocalConfig.deposit?.hasDepositLimitReachedModal) {
                component.openLimitsReachedModal();
            } else if (error instanceof DepositError && shellLocalConfig.hasImprovedDepositError) {
                component.setModal({
                    code: ModalCode.depositResult,
                    data: {
                        isSuccessful: false,
                        errorMessage: error.message,
                    },
                });
            } else {
                component.showError(error);
            }
        },
    });
    function setLoadingStates(isLoading: boolean, isBancontactLoading: boolean): void {
        component.isLoading = isLoading;
        component.isBancontactLoading = isBancontactLoading;
    }

    setLoadingStates(false, false);
}

export async function processFinalDepositResult(component: any, event: any) {
    const { origin, data } = event;

    const isValidSource = origin === window.location.origin && (data.source === 'paymentGatewayStatus' || data.source === 'monriResponse');

    if (!isValidSource || data.closeModal) {
        return;
    }

    if (data.openStatusModal) {
        component.openDepositStatusModal(data.status);
    }

    await processFinalDepositAnalytics(data.transactionData, component.user, component.userHasMadeDeposit, component.setUserDidDeposit);
}

export async function processFinalDepositAnalytics(
    transactionDto: OnlineTransactionDto,
    user: User,
    userHasMadeDeposit: boolean,
    setUserDidDeposit: () => void,
) {
    const transaction = processOnlineTransactionResponse(
        transactionDto,
        {
            user: user,
            didUserDepositBefore: userHasMadeDeposit,
        },
    );

    if (!transaction) {
        return;
    }

    const { event, payload } = transaction;
    if (event === AnalyticsEvent.Deposit) {
        if (payload?.event === AnalyticsEvent.DepositCompleteFD) {
            setUserDidDeposit();

            if (getConfig().countrySpecific.user.isUpdateCustomParamsEnabled) {
                await UserService.getInstance().updateCustomParams(marketingSourceService.getAssembledMarketingAttributionData(ftdAttributionHistory));
            }
        }

        AnalyticsSDK.getInstance().log(
            payload.event === AnalyticsEvent.DepositCompleteFD ? 'Deposit_First_Time_Deposit' : 'Deposit_Successful', {
                cashierVersion: 'web',
                balance: payload.balance.toString(),
                depositAmount: payload.depositAmount,
                depositMethod: payload.depositMethod,
                depositStatus: payload.depositStatus,
                event: payload.event,
                hashEmail: await sha256(payload.email),
                transactionId: payload.transactionId,
                transactionProvider: payload.transactionProvider,
                currency: '',
            },
        );

        if (shellLocalConfig.seonConfig.transactions.shouldSendSeparateCall) {
            await antiFraudService.sendTransaction({
                type: TransactionType.Deposit,
                method: payload.transactionProvider,
                transactionData: transaction.transactionDto as Record<string, unknown>,
            });
            antiFraudService.renewSession();
        }
    } else if (event === AnalyticsEvent.DepositError) {
        AnalyticsSDK.getInstance()
            .log('Deposit_Online_Errors', {
                cashierVersion: 'web',
                transactionId: payload.transactionId || '',
            });
    }
}

export function createTransactionDtoForQuickDeposit(payment: Payment, amount: number): PixDepositDto {
    return {
        kind: TransactionDtoType.PixDeposit,
        provider: payment.paymentProvider,
        transactionStatus: payment.status === PaymentStatus.Approved ? 'success' : 'failed',
        PPP_TransactionID: payment.paymentId,
        productId: 'Account Deposit',
        payment_method: 'cc_card',
        totalAmount: amount.toString(),
        messageForAnalytics: payment.status === PaymentStatus.Approved ? 'transaction succeed' : 'transaction failed',
    };
}
